export const FILTER_GROUPS = [
  'Product',
  'Customer',
  'Marketing',
  'Sale attributes',
  'LuxPlus',
  'Dates',
  'View',
  'Calculation settings',
  'Other filters',
] as const;

export type FilterGroup = (typeof FILTER_GROUPS)[number];

/** Omitted multiple means multi-select in the Lens contract. */
export function isSingleFilter(multiple: boolean | undefined): boolean {
  return multiple === false;
}

/** Presentation taxonomy only. Field definitions and members still come from the report. */
export function filterGroupFor(key: string, label: string, role?: string): FilterGroup {
  const name = `${key} ${label}`.toLowerCase().replace(/[_+/-]+/g, ' ');
  if (/pay at property|margin override|refunds? basis|addon migration/.test(name)) {
    return 'Calculation settings';
  }
  if (role || /data split|date cadence|table cp|time split|metric/.test(name)) {
    return 'View';
  }
  if (/primary date|check.?in date|period|date range/.test(name)) {
    return 'Dates';
  }
  if (/luxplus|lux plus|lp benefits|lp member|lp offer|lp eligibility/.test(name)) {
    return 'LuxPlus';
  }
  if (/traffic|mkt |campaign|affiliate|promo|bo sales|bo offer|bo campaign/.test(name)) {
    return 'Marketing';
  }
  if (/offer market manager|offer owner|\bpcm\b/.test(name)) {
    return 'Product';
  }
  if (
    /user |customer|signup|website|device|tenant|sfmc|gender|age group|first page/.test(name) ||
    /\bmarket\b/.test(name)
  ) {
    return 'Customer';
  }
  if (
    /product|offer|category|destination|vendor|property|brand|chain|supplier|tier|territory|villas|nha|holiday|season|spp|pcm/.test(
      name,
    )
  ) {
    return 'Product';
  }
  if (
    /sale|order|charter|currency|item status|deposit|reservation|traveller|pax|airline|airport|cabin|agency|lebt|cs agent|domestic.*int/.test(
      name,
    )
  ) {
    return 'Sale attributes';
  }
  return 'Other filters';
}

/** All is an unrestricted scope, not a snapshot of the available option list. */
export function includedAfterToggle(picked: Array<string>, options: Array<string>, value: string): Array<string> {
  const included = picked.length ? picked : options;
  const next = included.includes(value) ? included.filter((entry) => entry !== value) : [...included, value];
  return options.length && next.length === options.length && options.every((entry) => next.includes(entry)) ? [] : next;
}

export function complementSummary(picked: Array<string>, options: Array<string>): string | undefined {
  if (!picked.length || !options.length || picked.some((value) => !options.includes(value))) {
    return undefined;
  }
  const excluded = options.filter((value) => !picked.includes(value));
  if (excluded.length > 0 && excluded.length <= 2 && excluded.length < picked.length) {
    return `All except ${excluded.join(', ')}`;
  }
  return undefined;
}

export function filterPriority(group: FilterGroup, label: string): number {
  const priority: Partial<Record<FilterGroup, Array<string>>> = {
    Product: ['Product Name', 'Brand Name', 'Category Name', 'Offer Country', 'Property / vendor'],
    Customer: ['User Country', 'Website Region', 'Customer Type', 'User Device Category'],
    Marketing: ['Traffic Channel', 'Traffic Channel Detail', 'Mkt Source'],
  };
  const index = priority[group]?.findIndex((entry) => entry.toLowerCase() === label.toLowerCase()) ?? -1;
  return index < 0 ? 100 : index;
}
